import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import QuoteModal from '../components/QuoteModal';
import services from '../data/services';
import heroImage from '../assets/generated/hero-office-modern.png';

const features = [
  {
    title: 'Planned for real offices',
    text: 'Clean networking, practical security, and support systems designed around how teams actually work.'
  },
  {
    title: 'One partner, many services',
    text: 'From structured cabling to repairs and annual maintenance, your setup stays consistent across every touchpoint.'
  },
  {
    title: 'Local and responsive',
    text: 'Fast communication and hands-on support for businesses in Mira Road and Mumbai.'
  }
];

const processSteps = [
  {
    title: 'Understand the need',
    text: 'We start with the office environment, the business priority, and what needs to improve first.'
  },
  {
    title: 'Design the right setup',
    text: 'We align networking, security, backup, and device support into a solution that makes sense together.'
  },
  {
    title: 'Support the long run',
    text: 'We stay involved through maintenance, upgrades, repairs, and future growth.'
  }
];

export default function Home() {
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  return (
    <>
      <section className="hero-section">
        <div className="container hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">New-Generation Office IT</span>
            <h1>Modern infrastructure, security, and support for businesses that want systems done properly.</h1>
            <p className="hero-sub">
              i-Sha Infosys helps offices across Mira Road and Mumbai build reliable networks,
              protect data, secure premises, and keep devices running without the usual chaos.
            </p>

            <div className="hero-actions">
              <button className="btn" onClick={() => setShowQuoteModal(true)}>
                Request a Quote
              </button>
              <Link to="/services" className="btn btn-secondary">
                View Services
              </Link>
            </div>

            <div className="hero-metrics">
              <div>
                <strong>01</strong>
                <span>Networking and infrastructure</span>
              </div>
              <div>
                <strong>02</strong>
                <span>Security, repairs, and recovery</span>
              </div>
              <div>
                <strong>03</strong>
                <span>Maintenance that stays proactive</span>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-image-shell">
              <img src={heroImage} alt="Modern office technology environment" className="hero-image" />
            </div>
            <div className="floating-card floating-card-top">
              <span className="mini-label">Secure by design</span>
              <p>Office networking, CCTV, storage, and support aligned under one modern setup.</p>
            </div>
            <div className="floating-card floating-card-bottom">
              <span className="mini-label">Built for uptime</span>
              <p>Cleaner planning, faster support, and fewer avoidable breakdowns.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container section section-tight">
        <div className="info-strip">
          <div>
            <span className="strip-label">Coverage</span>
            <p>Pan Mumbai business support</p>
          </div>
          <div>
            <span className="strip-label">Core work</span>
            <p>Infrastructure, security, repairs, storage, and maintenance</p>
          </div>
          <div>
            <span className="strip-label">Approach</span>
            <p>Professional, practical, and easy to work with</p>
          </div>
        </div>
      </section>

      <section className="container section">
        <div className="section-header">
          <span className="eyebrow">Why It Feels Better</span>
          <h2>A cleaner, more modern way to handle business IT.</h2>
          <p>
            We bring structure to the parts of office technology that usually feel reactive,
            fragmented, or difficult to maintain.
          </p>
        </div>

        <div className="feature-grid">
          {features.map((feature) => (
            <article key={feature.title} className="feature-card">
              <span className="feature-badge">{feature.title.slice(0, 2).toUpperCase()}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="container section">
        <div className="section-header section-header-row">
          <div>
            <span className="eyebrow">Service Highlights</span>
            <h2>Every major office IT need, presented clearly.</h2>
          </div>
          <Link to="/services" className="text-link">
            Explore all services
          </Link>
        </div>

        <div className="service-grid">
          {services.map((service) => (
            <article key={service.id} className="service-card">
              <img src={service.image} alt={service.title} className="service-thumb" />
              <div className="service-card-body">
                <span className="service-tag">{service.category}</span>
                <h3>{service.title}</h3>
                <p>{service.short}</p>
                <Link to={`/services/${service.id}`} className="text-link">
                  View service
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container section">
        <div className="cta-panel">
          <div className="cta-panel-copy">
            <span className="eyebrow">How We Work</span>
            <h2>From urgent fixes to full office rollouts, the process stays simple and professional.</h2>
            <p>
              We help businesses move from fragmented support to a setup that feels planned,
              dependable, and easier to manage.
            </p>
          </div>

          <div className="process-list">
            {processSteps.map((step, index) => (
              <article key={step.title} className="process-card">
                <span className="process-number">0{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container section">
        <div className="contact-band">
          <div>
            <span className="eyebrow">Ready To Upgrade?</span>
            <h2>Let&apos;s make your office technology look sharper and work harder.</h2>
            <p>Tell us whether you are planning a new setup, troubleshooting a problem, or improving an existing one.</p>
          </div>
          <div className="contact-band-actions">
            <Link to="/contact" className="btn">
              Contact Us
            </Link>
            <button className="btn btn-outline-accent" onClick={() => setShowQuoteModal(true)}>
              Get a Quote
            </button>
          </div>
        </div>
      </section>

      {showQuoteModal && <QuoteModal onClose={() => setShowQuoteModal(false)} />}
    </>
  );
}
